// Tela de manutenção (mostrada a quem não é administrador quando o modo manutenção está ligado).
// Extraído de App.js sem alteração de comportamento.
import React from "react";
import { useIsMobile } from "../lib/theme";
import { GlockIcon } from "../components/icons";
import { _maintenanceMessage, _maintenanceEta } from "../lib/maintenance";
import { authPalette, AuthBackground } from "../components/authUi";

export function MaintenanceScreen({ message, eta }) {
  const P = authPalette();
  const isMobile = useIsMobile();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => { setTimeout(() => setMounted(true), 60); }, []);
  const msg = message || _maintenanceMessage;
  const etaStr = eta || _maintenanceEta;

  // ── Glock interativa — idêntica ao login ──────────────────
  const glockRef   = React.useRef(null);
  const animRef    = React.useRef({ tiltX:0, tiltY:0, scale:1, glowIntensity:0, particles:[], hovered:false });
  const rafRef     = React.useRef(null);

  React.useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); }, []);

  function spawnParticles() {
    const s = animRef.current; const count = 18;
    for (let i = 0; i < count; i++) {
      const angle = (i/count)*Math.PI*2 + Math.random()*0.4;
      const speed = 1.8 + Math.random()*2.8;
      s.particles.push({ x:0, y:0, vx:Math.cos(angle)*speed, vy:Math.sin(angle)*speed, life:1, size:2+Math.random()*3 });
    }
  }

  function onMouseEnter() { const s=animRef.current; if (!s.hovered){s.hovered=true;spawnParticles();} animate(); }
  function onMouseLeave() { animRef.current.hovered = false; }
  function onMouseMove(e) {
    const el = glockRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const dx = (e.clientX - (r.left+r.width/2)) / (r.width/2);
    const dy = (e.clientY - (r.top+r.height/2)) / (r.height/2);
    animRef.current.targetTiltX = dy*22; animRef.current.targetTiltY = -dx*22;
  }

  function animate() {
    const s = animRef.current; const el = glockRef.current; if (!el) return;
    s.tiltX = (s.tiltX||0) + ((s.targetTiltX||0)-(s.tiltX||0))*0.12;
    s.tiltY = (s.tiltY||0) + ((s.targetTiltY||0)-(s.tiltY||0))*0.12;
    const tScale = s.hovered?1.12:1, tGlow=s.hovered?1:0;
    s.scale        = (s.scale||1)       + (tScale-(s.scale||1))*0.1;
    s.glowIntensity= (s.glowIntensity||0)+(tGlow-(s.glowIntensity||0))*0.1;
    if (!s.hovered){s.targetTiltX=0;s.targetTiltY=0;}
    s.particles = s.particles.filter(p=>p.life>0.01);
    s.particles.forEach(p=>{p.x+=p.vx;p.y+=p.vy;p.vx*=0.94;p.vy*=0.94;p.life*=0.92;});
    const gi=s.glowIntensity;
    el.style.transform=`perspective(400px) rotateX(${s.tiltX}deg) rotateY(${s.tiltY}deg) scale(${s.scale}) rotate(-8deg)`;
    el.style.filter=`drop-shadow(0 0 ${18+gi*28}px rgba(${P.rgb},${0.5+gi*0.5})) drop-shadow(0 0 ${40+gi*60}px rgba(${P.rgb},${0.2+gi*0.4}))`;
    const cvs = el.querySelector("canvas.maint-particles");
    if (cvs) {
      const ctx=cvs.getContext("2d"); ctx.clearRect(0,0,cvs.width,cvs.height);
      const cx=cvs.width/2, cy=cvs.height/2;
      s.particles.forEach(p=>{
        ctx.beginPath(); ctx.arc(cx+p.x*4,cy+p.y*4,p.size*p.life,0,Math.PI*2);
        ctx.fillStyle=`rgba(${P.rgbLt},${p.life*0.9})`; ctx.fill();
        ctx.beginPath(); ctx.arc(cx+p.x*4-p.vx*3,cy+p.y*4-p.vy*3,p.size*p.life*0.4,0,Math.PI*2);
        ctx.fillStyle=`rgba(${P.rgb},${p.life*0.4})`; ctx.fill();
      });
    }
    const still = s.hovered||Math.abs(s.tiltX)>0.1||Math.abs(s.tiltY)>0.1||Math.abs(s.scale-1)>0.002||s.glowIntensity>0.01||s.particles.length>0;
    if (still) rafRef.current=requestAnimationFrame(animate);
    else { el.style.transform=""; el.style.filter=""; }
  }
  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes maint-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.7;transform:scale(1.04)} }
        @keyframes maint-float { 0%,100%{transform:translateY(0) rotate(-5deg)} 50%{transform:translateY(-12px) rotate(-5deg)} }
        @keyframes maint-glow { 0%,100%{filter:drop-shadow(0 0 18px rgba(${P.rgb},0.5)) drop-shadow(0 0 40px rgba(${P.rgb},0.2))} 50%{filter:drop-shadow(0 0 32px rgba(${P.rgb},0.9)) drop-shadow(0 0 70px rgba(${P.rgb},0.4))} }
        @keyframes maint-scan { 0%{transform:translateY(-100%);opacity:0} 8%{opacity:0.45} 92%{opacity:0.45} 100%{transform:translateY(2000%);opacity:0} }
        @keyframes orb1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(30px,-20px)} }
        @keyframes orb2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-25px,18px)} }
        @keyframes dot-blink { 0%,80%,100%{opacity:0.2;transform:scale(0.8)} 40%{opacity:1;transform:scale(1)} }
      `}</style>

      {/* Background */}
      <div style={{ position:"fixed", inset:0, background:P.pageBg, zIndex:0 }} />
      <div style={{ position:"fixed", inset:0, overflow:"hidden", zIndex:0, pointerEvents:"none" }}>
        <div style={{ position:"absolute", width:500, height:500, borderRadius:"50%", top:"5%", left:"5%", background:`radial-gradient(circle, rgba(${P.rgb},0.07) 0%, transparent 70%)`, animation:"orb1 16s ease-in-out infinite", filter:"blur(40px)" }} />
        <div style={{ position:"absolute", width:400, height:400, borderRadius:"50%", bottom:"10%", right:"8%", background:`radial-gradient(circle, rgba(${P.rgb},0.05) 0%, transparent 70%)`, animation:"orb2 20s ease-in-out infinite", filter:"blur(50px)" }} />
        <div style={{ position:"absolute", left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,rgba(${P.rgb},0.2),transparent)`, animation:"maint-scan 10s linear infinite", top:0 }} />
      </div>
      <AuthBackground />

      <div style={{ height:"100vh", overflow:"hidden", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:P.fontLayout, padding:"2rem", position:"relative", zIndex:1 }}>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", textAlign:"center", gap:"2rem", maxWidth:500, width:"100%",
          opacity: mounted ? 1 : 0, transform: mounted ? "translateY(0)" : "translateY(20px)", transition:"all 0.6s ease" }}>

          {/* Glock interativa — idêntica ao login */}
          <style>{`
            @keyframes float { 0%,100%{transform:translateY(0) rotate(-8deg)} 50%{transform:translateY(-10px) rotate(-8deg)} }
            @keyframes glow-pulse { 0%,100%{filter:drop-shadow(0 0 18px rgba(${P.rgb},0.5)) drop-shadow(0 0 40px rgba(${P.rgb},0.2))} 50%{filter:drop-shadow(0 0 28px rgba(${P.rgb},0.8)) drop-shadow(0 0 60px rgba(${P.rgb},0.35))} }
            .maint-glock:not(:hover) .maint-glock-el { animation: float 4s ease-in-out infinite, glow-pulse 3s ease-in-out infinite; }
            .maint-glock { cursor: crosshair; }
          `}</style>
          <div className="maint-glock" style={{ display:"inline-block", position:"relative", padding:"1rem" }}
            onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave} onMouseMove={onMouseMove}>
            <canvas className="maint-particles" width={220} height={220}
              style={{ position:"absolute", inset:0, margin:"auto", top:"-50%", left:"-50%", right:"-50%", bottom:"-50%", width:220, height:220, pointerEvents:"none", zIndex:2 }} />
            <div ref={glockRef} className="maint-glock-el"
              style={{ display:"inline-block", transformOrigin:"center center", willChange:"transform,filter", position:"relative", zIndex:3 }}>
              <GlockIcon size={92} color={P.ac} />
            </div>
          </div>

          {/* Card */}
          <div style={{ width:"100%", background:P.cardBg, border:`1px solid rgba(${P.rgb},0.18)`, borderRadius:22, padding:isMobile?"1.8rem 1.3rem":"2.4rem 2.8rem", backdropFilter:"blur(24px)", boxShadow:P.cardShadow, position:"relative", overflow:"hidden" }}>
            {/* Scan line inside card */}
            <div style={{ position:"absolute", left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,rgba(${P.rgb},0.35),transparent)`, animation:"maint-scan 5s ease-in-out infinite 1s", top:0 }} />

            <div style={{ fontFamily:P.fontDisplay, fontWeight:900, fontSize:"2.2rem", color:P.text, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:"0.5rem" }}>
              Em Manutenção
            </div>
            <p style={{ fontSize:"0.9rem", color:P.dim, margin:"0 0 1.8rem", lineHeight:1.7, whiteSpace:"pre-line" }}>{msg}</p>

            {/* ETA badge */}
            <div style={{ display:"inline-flex", alignItems:"center", gap:"0.7rem", background:`rgba(${P.rgb},0.08)`, border:`1px solid rgba(${P.rgb},0.2)`, borderRadius:12, padding:"0.75rem 1.4rem" }}>
              <div style={{ display:"flex", gap:4 }}>
                {[0,1,2].map(i => <div key={i} style={{ width:6, height:6, borderRadius:"50%", background:P.ac, animation:`dot-blink 1.4s ease-in-out ${i*0.22}s infinite` }} />)}
              </div>
              <span style={{ color:P.acLight, fontSize:"0.88rem", fontWeight:600 }}>Previsão: {etaStr}</span>
            </div>
          </div>

          {/* Footer */}
          <div style={{ display:"flex", alignItems:"center", gap:"0.5rem", opacity:0.6 }}>
            <GlockIcon size={18} color={P.ac} />
            <span style={{ fontFamily:P.fontDisplay, fontWeight:800, fontSize:"0.85rem", color:P.ac, letterSpacing:"0.12em", textTransform:"uppercase" }}>G17 App</span>
          </div>
        </div>
      </div>
    </>
  );
}
